<script setup lang="ts">
import { RefreshCw, MapPin, DollarSign, ArrowDownNarrowWide } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import DeliveryTeamOffer from '@/widgets-iframe/DeliveryTeamOffer.vue';
import { toast } from '@/ui-plus/sonner';
import { ref, computed, onMounted, onUnmounted } from 'vue';
import type { ResType } from '@api-client/typeUtils';
import { getMeasure } from '@lib/utils/getMeasure';
import { courierRequestState_perOrderRef } from './state';
import { storeOrdersApiClient } from '@api-client/store_orders';
import { useDialog } from "@/ui-plus/dialog/use-dialog";
import CourierOffer from './CourierOffer.vue';
import { courierRequestEvents, getCloseCourierDialogEventName, type CloseCourierDialogDetail } from './events';

const props = defineProps<{
    storeOrderId: string;
    storeId: string;
    country: string;
}>();

const dialog = useDialog();
const activeOfferDialogId = ref<string | null>(null);

function onCloseCourierDialog(e: Event) {
    if (!activeOfferDialogId.value) return;
    const { deliveryteamOrderId } = (e as CustomEvent<CloseCourierDialogDetail>).detail;
    dialog.close(activeOfferDialogId.value, deliveryteamOrderId);
    activeOfferDialogId.value = null;
}

type CourierType = NonNullable<
    ResType<typeof storeOrdersApiClient.nearby_couriers[':storeId']['$post']>["data"]["couriers"]
>[number];


// --- Sort & range ---
const RANGE_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20];
const sortBy = ref<'distance' | 'price'>('distance');
const sortDir = ref<'asc' | 'desc'>('asc');
const currentRange = ref(1);
const isLoading = ref(false);
const foundCouriers = ref<CourierType[]>([]);
const sortedCouriers = computed(() => {
    return foundCouriers.value
        .filter(courier => {
            // if (isScheduling.value) {
            //     return true;
            // }
            return courier.isOpen === true;
        })
        .sort((a, b) => {
            // Open couriers first (treat missing isOpen as closed)
            const openA = a.isOpen === true ? 1 : 0;
            const openB = b.isOpen === true ? 1 : 0;
            if (openA !== openB) {
                return openB - openA; // higher "open" score first → true before false/undefined
            }
            const distA = a.distance ?? Infinity;
            const distB = b.distance ?? Infinity;
            const priceA = a.breakdown?.buyerPays ?? Infinity;
            const priceB = b.breakdown?.buyerPays ?? Infinity;
            const dir = sortDir.value === 'asc' ? 1 : -1;
            if (sortBy.value === 'price') {
                return priceA !== priceB ? (priceA - priceB) * dir : distA - distB;
            }
            return distA !== distB ? (distA - distB) * dir : priceA - priceB;
        });
});

const isLoadingSkeletonVisible = computed(() => {
    return isLoading.value;
});

const onSearchRangeChange = (e: Event) => {
    const value = Number((e.target as HTMLSelectElement).value);
    currentRange.value = value;
    void loadNearbyCouriers();
};

const onSortClick = (by: 'distance' | 'price') => {
    if (sortBy.value === by) {
        sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
    } else {
        sortBy.value = by;
        sortDir.value = 'asc';
    }
};
// nearby_couriers
const loadNearbyCouriers = async () => {
    const state = courierRequestState_perOrderRef.value[props.storeOrderId];

    isLoading.value = true;
    foundCouriers.value = [];
    if (!state.pickupAddress || !state.deliveryAddress) {
        toast.error('Missing required parameters');
        isLoading.value = false;
        return
    };

    if (!state.pickupAddress || !state.deliveryAddress) return;
    const measure = getMeasure(state.pickupAddress.country ?? '');
    const nearbyCouriersData = await storeOrdersApiClient.nearby_couriers[':storeId'].$post({
        param: { storeId: props.storeId },
        json: {
            pickupLat: state.pickupAddress.lat,
            pickupLng: state.pickupAddress.lng,
            deliveryLat: state.deliveryAddress.lat,
            deliveryLng: state.deliveryAddress.lng,
            // sortBy: sortBy.value, // useless bc client already sorting
            // sortDir: sortDir.value,
            measure,
            searchRange: currentRange.value,
        },
    }).then((resp) => resp.json()).then((r) => r.data);

    foundCouriers.value = nearbyCouriersData.couriers;
    currentRange.value = nearbyCouriersData.lastAutoRange;

    isLoading.value = false;
}

onMounted(() => {
    sortBy.value = courierRequestState_perOrderRef.value[props.storeOrderId].cachedStoreData?.deliveryOptions?.courierSortBy ?? 'distance';
    currentRange.value = courierRequestState_perOrderRef.value[props.storeOrderId].cachedStoreData?.deliveryOptions?.courierSearchRadius ?? 1;
    courierRequestEvents.addEventListener(getCloseCourierDialogEventName(props.storeOrderId), onCloseCourierDialog);
    setTimeout(() => {
        loadNearbyCouriers();
    }, 100);
});
onUnmounted(() => {
    courierRequestEvents.removeEventListener(getCloseCourierDialogEventName(props.storeOrderId), onCloseCourierDialog);
});

function openOfferDetails(courier: CourierType) {
    const dialogId = `courier-offer-${props.storeOrderId}-${courier.courierId}`;
    activeOfferDialogId.value = dialogId;
    dialog.show<number | undefined>({
        id: dialogId,
        title: courier.deliveryteamName,
        closeable: true,
        component: CourierOffer,
        props: {
            courier: courier,
            state: courierRequestState_perOrderRef.value[props.storeOrderId],
            storeId: props.storeId,
            storeOrderId: props.storeOrderId,
        },
        onSuccess: async () => {
            console.log('onSuccess', arguments);
        },
        onCancel: async () => {
            console.log('onCancel', arguments);
        },
        onError: async () => {
            console.log('onError', arguments);
        }
    });
}
</script>

<template>
    <div>
        <h3 class="text-2xl font-bold">
            Offers {{ isLoadingSkeletonVisible ? '' : `(${sortedCouriers.length})` }}
            <Button variant="ghost"
                    size="icon"
                    @click="loadNearbyCouriers()"
                    :disabled="isLoading">
                <RefreshCw class="h-4 w-4" />
            </Button>
        </h3>
        <div v-if="isLoadingSkeletonVisible"
             class="flex flex-col gap-3 py-2"
             aria-busy="true"
             aria-label="Loading offers">
            <div v-for="n in 2"
                 :key="n"
                 class="rounded-lg border border-border/60 p-4 space-y-3">
                <div class="flex gap-2 pt-1">
                    <div class="h-8 w-20 rounded-md bg-muted animate-pulse" />
                    <div class="h-8 w-24 rounded-md bg-muted/80 animate-pulse" />
                </div>
            </div>
        </div>
        <div v-else>
            <div class="flex items-center gap-2 overflow-x-auto max-w-full">
                <select @change="onSearchRangeChange($event)"
                        :disabled="isLoading"
                        class="border-input ring-offset-background data-[placeholder]:text-muted-foreground focus:ring-ring my-1 flex h-9 items-center justify-between whitespace-nowrap rounded-md border bg-transparent px-3 py-2 text-start text-sm shadow-sm focus:outline-none focus:ring-1 disabled:cursor-not-allowed disabled:opacity-50"
                        title="bounding box to search for stores">
                    <option v-for="val in RANGE_OPTIONS"
                            :key="val"
                            :selected="currentRange === val"
                            :value="val">
                        Range: {{ val }} {{ getMeasure(props.country ?? '') }}
                    </option>
                </select>
                <!-- <div
                     class="bg-muted text-muted-foreground w-fit flex items-center justify-center rounded-lg p-1 gap-2 my-1">
                    <Button variant="ghost"
                            @click="isScheduling = false"
                            :class="!isScheduling ? 'bg-background text-foreground shadow hover:bg-background/80' : ''"
                            size="sm">
                        Open Now
                    </Button>
                    <Button variant="ghost"
                            @click="isScheduling = true"
                            :class="isScheduling ? 'bg-background text-foreground shadow hover:bg-background/80' : ''"
                            size="sm">
                        Scheduling
                    </Button>
                </div> -->
            </div>
            <div
                 class="bg-muted text-muted-foreground w-fit flex items-center justify-center rounded-lg p-1 gap-1 my-1">
                <Button variant="ghost"
                        size="sm"
                        type="button"
                        @click="onSortClick('distance')"
                        :class="sortBy === 'distance' ? 'bg-background text-foreground shadow hover:bg-background/80' : ''"
                        title="Sort by distance">
                    <span class="inline-flex items-center gap-0.5">
                        <MapPin class="h-4 w-4 shrink-0" />
                        <ArrowDownNarrowWide v-if="sortBy === 'distance'"
                                             class="h-3.5 w-3.5 shrink-0 transition-transform"
                                             :class="sortDir === 'desc' ? 'rotate-180' : ''" />
                    </span>
                </Button>
                <Button variant="ghost"
                        size="sm"
                        type="button"
                        @click="onSortClick('price')"
                        :class="sortBy === 'price' ? 'bg-background text-foreground shadow hover:bg-background/80' : ''"
                        title="Sort by price">
                    <span class="inline-flex items-center gap-0.5">
                        <DollarSign class="h-4 w-4 shrink-0" />
                        <ArrowDownNarrowWide v-if="sortBy === 'price'"
                                             class="h-3.5 w-3.5 shrink-0 transition-transform"
                                             :class="sortDir === 'desc' ? 'rotate-180' : ''" />
                    </span>
                </Button>
            </div>
        </div>


        <div class="flex flex-col gap-2"
             v-if="sortedCouriers.length > 0">
            <div class="flex flex-col gap-2 py-2">
                <DeliveryTeamOffer :courier="courier"
                                   @open-offer-details="(courier: CourierType) => openOfferDetails(courier)"
                                   v-for="courier in sortedCouriers"
                                   :key="courier.courierId" />
            </div>
        </div>
    </div>
</template>